import { User } from "lucide-react";

import { Alert, AlertDescription } from "@src/shared/components/ui/alert";
import { Input } from "@src/shared/components/ui/input";
import { Label } from "@src/shared/components/ui/label";
import { cn } from "@src/shared/lib/utils";

import { UserEntity } from "../../../domain";
import type { SignUpAccountValues } from "../../../domain/v2";

export function AccountProfileStep({
  values,
  error,
  isSubmitting,
  isEmailLocked = false,
  onChange,
}: {
  values: SignUpAccountValues;
  error?: string | null;
  isSubmitting?: boolean;
  /**
   * Invitations are bound to the address they were sent to, so the account is
   * always created for that address regardless of what is typed here.
   */
  isEmailLocked?: boolean;
  onChange: (values: SignUpAccountValues) => void;
}) {
  const initials = UserEntity.initials(values);
  const update = <K extends keyof SignUpAccountValues>(
    key: K,
    value: SignUpAccountValues[K],
  ) => onChange({ ...values, [key]: value });

  return (
    <div className="w-full max-w-[370px] rounded-[18px] border border-border bg-background p-[22px] text-center shadow-[0_18px_50px_rgba(20,23,26,0.10)]">
      <div
        className={cn(
          "mx-auto mb-4 flex size-[60px] items-center justify-center rounded-full border text-[21px] font-semibold transition-colors",
          initials
            ? "border-foreground bg-foreground text-background"
            : "border-dashed border-border bg-muted text-muted-foreground",
        )}
      >
        {initials || <User className="size-6" strokeWidth={1.9} aria-hidden />}
      </div>

      {error ? (
        <Alert
          className="mb-3 border-destructive/40 bg-destructive/10 text-left"
          variant="destructive"
        >
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-2 sm-stitch:grid-cols-2">
        <div>
          <Label className="sr-only" htmlFor="sign-up-first-name">
            First name
          </Label>
          <Input
            autoComplete="given-name"
            className="h-[42px] rounded-[9px] text-center text-[13.5px]"
            disabled={isSubmitting}
            id="sign-up-first-name"
            onChange={(event) => update("firstName", event.target.value)}
            placeholder="First name"
            required
            value={values.firstName}
          />
        </div>
        <div>
          <Label className="sr-only" htmlFor="sign-up-last-name">
            Last name
          </Label>
          <Input
            autoComplete="family-name"
            className="h-[42px] rounded-[9px] text-center text-[13.5px]"
            disabled={isSubmitting}
            id="sign-up-last-name"
            onChange={(event) => update("lastName", event.target.value)}
            placeholder="Last name"
            required
            value={values.lastName}
          />
        </div>
      </div>

      <div className="mt-2 grid gap-2">
        <div>
          <Label className="sr-only" htmlFor="sign-up-email">
            Email
          </Label>
          <Input
            autoComplete="email"
            className="h-[42px] rounded-[9px] text-center text-[13.5px]"
            disabled={isSubmitting}
            id="sign-up-email"
            inputMode="email"
            onChange={(event) => update("email", event.target.value)}
            placeholder="you@practice.com"
            readOnly={isEmailLocked}
            required
            type="email"
            value={values.email}
          />
          {isEmailLocked ? (
            <p className="mt-1 text-[11px] leading-[1.45] text-muted-foreground">
              Your invitation was sent to this address.
            </p>
          ) : null}
        </div>
        <div>
          <Label className="sr-only" htmlFor="sign-up-password">
            Password
          </Label>
          <Input
            autoComplete="new-password"
            className="h-[42px] rounded-[9px] text-center text-[13.5px]"
            disabled={isSubmitting}
            id="sign-up-password"
            minLength={10}
            onChange={(event) => update("password", event.target.value)}
            placeholder="Create a password"
            required
            type="password"
            value={values.password}
          />
        </div>
      </div>
    </div>
  );
}
